import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { BaseService } from 'src/app/tabms/services/base/base.service';
import { ThemeLayoutService } from 'src/app/@theme/services/theme-layout.service';
import { RTL } from 'src/app/@theme/const';
import { TabConst } from 'src/app/tabms/tab-consts';

@Component({
  selector: 'app-base-icon-selector',
  templateUrl: './base-icon-selector.component.html',
  styleUrl: './base-icon-selector.component.scss'
})
export class BaseIconSelectorComponent implements OnInit, OnDestroy {
  @ViewChild('triggerField', { read: ElementRef }) triggerField: ElementRef;

  @Input() control: FormControl;
  @Input() fieldName: string;
  @Input() colorHex: string = '#3498db';
  @Input() size: 'default' | 'compact' | 'xs' = TabConst.DefaultSize;

  showIconDropdown = false;
  iconSearchTerm = '';
  currentLang: string;
  dropdownStyle: { [key: string]: string } = {};
  private destroy$: Subject<void> = new Subject<void>();

  availableIcons: string[] = [
    'phone', 'phone-call', 'phone-incoming', 'phone-outgoing',
    'mail', 'mail-opened', 'send',
    'message', 'message-circle', 'messages',
    'calendar', 'calendar-event', 'calendar-time',
    'clock', 'alarm', 'hourglass',
    'user', 'users', 'user-plus',
    'briefcase', 'building', 'home',
    'file', 'file-text', 'files', 'folder',
    'clipboard', 'clipboard-list', 'clipboard-check',
    'check', 'check-circle', 'circle-check',
    'star', 'star-filled', 'heart',
    'flag', 'flag-filled', 'bookmark',
    'bell', 'bell-ringing', 'notification',
    'alert-circle', 'alert-triangle', 'info-circle',
    'settings', 'adjustments', 'tool',
    'search', 'zoom-in', 'eye',
    'edit', 'pencil', 'writing',
    'trash', 'archive', 'x',
    'plus', 'minus', 'dots',
    'link', 'external-link', 'share',
    'download', 'upload', 'cloud',
    'lock', 'lock-open', 'shield',
    'map-pin', 'location', 'world',
    'car', 'plane', 'walk',
    'coffee', 'gift', 'cake',
    'bulb', 'bolt', 'flame',
    'chart-bar', 'chart-line', 'chart-pie',
    'currency-dollar', 'receipt', 'wallet',
    'target', 'trophy', 'medal',
    'video', 'camera', 'photo',
    'microphone', 'headphones', 'music',
    'book', 'notebook', 'news',
    'tag', 'tags', 'hash',
    'list', 'list-check', 'checklist',
    'activity', 'heartbeat', 'pulse'
  ];

  filteredIcons: string[] = [];

  get iconColor(): string {
    return this.colorHex || '#3498db';
  }

  constructor(
    private baseService: BaseService,
    private themeLayoutService: ThemeLayoutService
  ) {
    this.currentLang = this.baseService.getCurrentLanguage();
    this.themeLayoutService.directionChange.pipe(takeUntil(this.destroy$)).subscribe((dir) => {
      this.currentLang = dir === RTL ? 'ar' : 'en';
    });
  }

  ngOnInit(): void {
    this.filteredIcons = [...this.availableIcons];
  }

  toggleDropdown(): void {
    this.showIconDropdown = !this.showIconDropdown;
    if (this.showIconDropdown) {
      this.iconSearchTerm = '';
      this.filteredIcons = [...this.availableIcons];
      this.updateDropdownPosition();
    }
  }

  updateDropdownPosition(): void {
    if (this.triggerField) {
      const rect = this.triggerField.nativeElement.getBoundingClientRect();
      const isRtl = this.currentLang === 'ar';

      this.dropdownStyle = {
        top: `${rect.bottom + 4}px`,
        ...(isRtl ? { right: `${window.innerWidth - rect.right}px` } : { left: `${rect.left}px` })
      };
    }
  }

  closeDropdown(): void {
    this.showIconDropdown = false;
  }

  filterIcons(): void {
    const search = this.iconSearchTerm.toLowerCase().trim();
    if (!search) {
      this.filteredIcons = [...this.availableIcons];
    } else {
      this.filteredIcons = this.availableIcons.filter(icon =>
        icon.toLowerCase().includes(search)
      );
    }
  }

  selectIcon(icon: string): void {
    this.control.setValue(icon);
    this.closeDropdown();
  }

  clearIcon(event: Event): void {
    event.stopPropagation();
    this.control.setValue(null);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
